import { useNavigate } from 'react-router-dom';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { useClientTranslation } from '@/lib/core/i18n';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { formatAmount } from '@/lib/core/utils';
import { getShopBagHref } from '@/services/platform/data/shop';
import { HeartToggle } from '@/components/shared/shop/wishlist/ui/HeartToggle';
import { MiniCardCarousel } from '@/components/shared/shop/ui/MiniCardCarousel';
import { ShopMiniCard } from '@/components/shared/shop/section/mini-card';
import type { CartProductItemDto, ProductListItemDto } from '@/services/platform/api/model';

interface ItemAddedSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item?: CartProductItemDto;
  suggestions: ProductListItemDto[];
  shopSectionSlug: string;
  /** Public GUID of the product the added item belongs to — used for the card link. */
  productGuid?: string;
  isFavorited?: boolean;
  onToggleFavorite?: () => void;
}

/**
 * Confirmation sheet shown right after a successful add-to-bag. Reflects the
 * chosen variant attributes and surfaces "View shopping bag" + related products.
 */
export function ItemAddedSheet({
  open,
  onOpenChange,
  item,
  suggestions,
  shopSectionSlug,
  productGuid,
  isFavorited = false,
  onToggleFavorite,
}: ItemAddedSheetProps) {
  const t = useClientTranslation();
  const navigate = useNavigate();
  const locale = useLocaleStore(state => state.locale);
  const { appSettings } = useApp();
  const localeSegment = locale?.split('-')[0] ?? 'en';

  const attributes = item?.options?.map(option => option.value).filter(Boolean) ?? [];
  const quantityLabel = `${t('App.Shop.QuantityAbbr')}: ${item?.quantity ?? 1}`;

  // Not offered at all when the bag has no page mapping — the item is already in the cart and the
  // bag icon in the header is the other way there, so a button that cannot resolve a destination is
  // better absent than pointed at a guessed URL.
  const bagHref = getShopBagHref(appSettings);

  const openShoppingBag = () => {
    if (!bagHref) return;
    onOpenChange(false);
    navigate(bagHref);
  };

  // Product detail routes are canonical without category/subcategory segments.
  const openProduct = (guid?: string | null) => {
    if (!guid) return;
    onOpenChange(false);
    navigate(`/${localeSegment}/shop/${shopSectionSlug}/discover/product_${guid}`);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle>
      <DrawerContent className="max-h-[85vh]">
        <div data-base-ui-swipe-ignore className="flex flex-1 flex-col gap-6 overflow-y-auto pt-4 pb-10.5">
          <h2 className="font-pp-neue-montreal text-jncquoi-neutral-1000 px-4 text-center text-[16px] leading-none font-normal">
            {t('App.Shop.ItemAddedToBag')}
          </h2>

          {item && (
            <div className="flex items-start gap-4 px-4">
              <button
                type="button"
                onClick={() => openProduct(productGuid ?? item.productId)}
                className="flex min-w-0 flex-1 items-start gap-4 text-left"
              >
                <div className="bg-jncquoi-neutral-50 relative h-24 w-20 shrink-0 overflow-hidden">
                  {item.imageUrl && (
                    <img
                      src={item.imageUrl}
                      alt={item.name ?? ''}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-none font-semibold tracking-[0.5px] uppercase">
                    {item.name}
                  </span>
                  <span className="font-pp-neue-montreal text-jncquoi-neutral-700 text-[12px] leading-tight font-normal tracking-[0.25px]">
                    {[...attributes, quantityLabel].filter(Boolean).join(' • ')}
                  </span>
                </div>
              </button>
              <HeartToggle
                isFavorited={isFavorited}
                size={24}
                onToggle={() => onToggleFavorite?.()}
                className="shrink-0 self-center"
              />
            </div>
          )}

          {bagHref && (
            <div className="px-4">
              <Button variant="primary-solid" palette="dark" className="w-full" onClick={openShoppingBag}>
                {t('App.Shop.ViewShoppingBag')}
              </Button>
            </div>
          )}

          {suggestions.length > 0 && (
            <MiniCardCarousel
              className="px-4"
              items={suggestions}
              ariaLabel={t('App.Shop.YouMayAlsoLike')}
              renderItem={product => (
                <ShopMiniCard
                  variant="product"
                  image={product.imageUrl ?? undefined}
                  brand={product.vendor ?? undefined}
                  name={product.title ?? undefined}
                  price={
                    product.price != null ? formatAmount(product.price, null, locale ?? 'en-GB') : undefined
                  }
                  onClick={() => openProduct(product.publicGuid)}
                />
              )}
            />
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
